import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { BrowserAuthError, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { graphConfig, loginRequest } from './authConfig';
import { requiredGroupId, adminGroupId, scopes, graphScopes, apiScope, clientId, apiPath } from './apiurl';

const AuthContext = createContext({});

const mapGraphProfile = (graphUser = {}, fallbackAccount = null) => ({
  id: graphUser.id || fallbackAccount?.localAccountId || '-',
  displayName: graphUser.displayName || fallbackAccount?.name || 'Guest',
  firstName: graphUser.givenName || fallbackAccount?.idTokenClaims?.given_name || '-',
  lastName: graphUser.surname || fallbackAccount?.idTokenClaims?.family_name || '-',
  email:
    graphUser.mail ||
    graphUser.userPrincipalName ||
    fallbackAccount?.username ||
    fallbackAccount?.idTokenClaims?.email ||
    '-',
  jobTitle: graphUser.jobTitle || fallbackAccount?.idTokenClaims?.jobTitle || '-',
  department: graphUser.department || fallbackAccount?.idTokenClaims?.department || '-',
  location: graphUser.officeLocation || fallbackAccount?.idTokenClaims?.location || '-',
  mobilePhone: graphUser.mobilePhone || fallbackAccount?.idTokenClaims?.phone_number || '-',
  businessPhone:
    Array.isArray(graphUser.businessPhones) && graphUser.businessPhones.length > 0
      ? graphUser.businessPhones[0]
      : '-',
});

export const AuthProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [account, setAccount] = useState(accounts?.[0] || null);
  const [userProfile, setUserProfile] = useState(null);
  const [authError, setAuthError] = useState('');
  const [role, setRole] = useState(() => sessionStorage.getItem('userRole') || null);
  const [authLoading, setAuthLoading] = useState(true);
  const lastRevalidationRef = useRef(0);
  // Ref to the latest revalidateMembership closure — lets the stable `revalidate`
  // callback (exposed via context) always call the current version without stale closures.
  const revalidateMembershipRef = useRef(null);

  const getActiveAccount = () => instance.getActiveAccount() || accounts?.[0] || null;

  const acquireProfileToken = async (activeAccount, forceRefresh = false) => {
    try {
      return await instance.acquireTokenSilent({
        ...loginRequest,
        account: activeAccount,
        scopes,
        forceRefresh,
      });
    } catch (error) {
      // Genuine re-auth required (MFA prompt, consent, expired session) — redirect is safe here.
      // Do NOT redirect on monitor_window_timeout: that error means the hidden iframe timed out
      // (third-party cookies blocked), and redirecting from inside validateGroupMembership would
      // create an infinite loop: redirect → Azure AD → handleRedirectPromise → dashboard → validate
      // → redirect → …  Let the caller decide what to do with other errors.
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...loginRequest, scopes });
        return null;
      }
      throw error;
    }
  };

  const fetchUserProfile = async (activeAccount) => {
    if (!activeAccount) return null;

    try {
      const tokenResponse = await acquireProfileToken(activeAccount);
      if (!tokenResponse) return null; // redirect in flight

      const graphResponse = await fetch(graphConfig.graphMeEndpoint, {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
      });

      if (!graphResponse.ok) {
        throw new Error(`Graph profile request failed with status ${graphResponse.status}`);
      }

      const graphUser = await graphResponse.json();
      const mappedProfile = mapGraphProfile(graphUser, activeAccount);

      setUserProfile(mappedProfile);
      sessionStorage.setItem('userInfo', JSON.stringify(mappedProfile));
      return mappedProfile;
    } catch (error) {
      console.error('Error loading Microsoft Graph user profile', error);
      const fallbackProfile = mapGraphProfile({}, activeAccount);
      setUserProfile(fallbackProfile);
      sessionStorage.setItem('userInfo', JSON.stringify(fallbackProfile));
      return fallbackProfile;
    }
  };

  // Returns { allowed: boolean, role: 'admin' | 'user' | null }
  // Pass forceRefresh=true when re-validating an existing session so token claims are current.
  const validateGroupMembership = async (activeAccount, forceRefresh = false) => {
    // App Role claims fast path — only used on first login (redirect response).
    // For session re-validation we always hit the Graph API so removal takes effect immediately
    // instead of waiting up to an hour for cached token claims to expire.
    if (!forceRefresh) {
      const roleClaims = activeAccount?.idTokenClaims?.roles;
      if (Array.isArray(roleClaims) && roleClaims.length > 0) {
        if (roleClaims.includes('PASS.Admin')) return { allowed: true, role: 'admin' };
        if (roleClaims.includes('PASS.User'))  return { allowed: true, role: 'user' };
        return { allowed: false, role: null };
      }
    }

    // When no group ID is configured we rely on App Roles.
    // On a force-refresh we must acquire a new token and read the fresh claims —
    // otherwise a user removed from an App Role stays allowed until their next
    // page load (the cached idTokenClaims above are already stale).
    if (!requiredGroupId) {
      if (!forceRefresh) {
        return { allowed: true, role: 'admin' };
      }
      try {
        // forceRefresh=false here: we don't need an iframe-force-refreshed token to read
        // App Role claims — acquireTokenSilent will renew via refresh token if the cache
        // has expired, which is safe.  Passing true would risk a monitor_window_timeout →
        // redirect loop (see acquireProfileToken comment above).
        const tokenResponse = await acquireProfileToken(activeAccount, false);
        if (!tokenResponse) return { allowed: false, role: null };
        const freshRoles = tokenResponse.idTokenClaims?.roles;
        if (Array.isArray(freshRoles) && freshRoles.length > 0) {
          if (freshRoles.includes('PASS.Admin')) return { allowed: true, role: 'admin' };
          if (freshRoles.includes('PASS.User'))  return { allowed: true, role: 'user' };
          return { allowed: false, role: null };
        }
        // No role claims in fresh token and no group ID — permissive fallback
        // (covers environments where neither mechanism is configured, e.g. dev).
        return { allowed: true, role: 'admin' };
      } catch {
        return { allowed: false, role: null };
      }
    }

    const resolveRole = (groupIds) => {
      const isAdmin = adminGroupId && groupIds.includes(adminGroupId);
      // Admin group grants access on its own — admins need not also be in the required group.
      if (!isAdmin && !groupIds.includes(requiredGroupId)) return { allowed: false, role: null };
      return { allowed: true, role: isAdmin ? 'admin' : 'user' };
    };

    // Group claims in the ID token are also stale — skip them when force-refreshing.
    if (!forceRefresh) {
      const groupClaims = activeAccount?.idTokenClaims?.groups;
      if (Array.isArray(groupClaims)) {
        return resolveRole(groupClaims);
      }
    }

    try {
      // Always acquire without force-refresh: the Graph API returns real-time membership
      // data regardless, so there's no need to force an iframe-based token refresh.
      // Passing true for forceRefresh risks monitor_window_timeout → acquireTokenRedirect
      // → redirect loop (see acquireProfileToken comment above).
      const tokenResponse = await acquireProfileToken(activeAccount, false);
      if (!tokenResponse) return { allowed: false, role: null }; // redirect in flight

      const graphResponse = await fetch(graphConfig.graphMemberOfEndpoint, {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
      });

      if (!graphResponse.ok) {
        throw new Error(`Graph request failed with status ${graphResponse.status}`);
      }

      const membership = await graphResponse.json();
      const groupIds = (membership?.value || []).map((item) => item.id);
      return resolveRole(groupIds);
    } catch (error) {
      console.error('Error validating group membership', error);
      // For transient errors (network, iframe timeout) fall back to the cached role so
      // the user isn't unexpectedly kicked out.  Background re-validations will retry.
      const cachedRole = sessionStorage.getItem('userRole');
      if (cachedRole) {
        return { allowed: true, role: cachedRole };
      }
      setAuthError('Unable to validate Azure AD group membership.');
      return { allowed: false, role: null };
    }
  };

  // ── Axios interceptors ────────────────────────────────────────────────────
  // Attaches auth tokens to every API request and handles 401/403 responses.
  // See getTokens() below for the two-token vs. legacy-ID-token strategy.
  //
  // useLayoutEffect (not useEffect) so interceptors are registered synchronously
  // before the browser paints — this guarantees they are in place before any
  // child component's useEffect fires its first API call.
  useLayoutEffect(() => {
    // Acquire the token to send as the Authorization header for our backend API.
    //
    // Two-token pattern (when REACT_APP_AZURE_API_SCOPE is set):
    //   • API access token  (aud = API app ID)  → Authorization header
    //   • Graph access token (aud = graph)       → X-Graph-Token header
    //
    // Legacy single-token pattern (no API scope configured):
    //   • ID token (aud = SPA client ID) → Authorization header
    //   • Graph access token             → X-Graph-Token header
    //   If idToken is empty (can happen before handleRedirectPromise resolves),
    //   force-refresh once to populate it.
    const getTokens = async (activeAccount, forceRefresh = false) => {
      if (apiScope) {
        // Two separate acquisitions — each targets its own resource.
        try {
          const [apiResp, graphResp] = await Promise.all([
            instance.acquireTokenSilent({ account: activeAccount, scopes: [apiScope], forceRefresh }),
            instance.acquireTokenSilent({ account: activeAccount, scopes: graphScopes, forceRefresh }),
          ]);
          return { authToken: apiResp?.accessToken, graphToken: graphResp?.accessToken };
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) {
            // User hasn't consented to the API scope yet (e.g. first login after scope was added).
            // Redirect for interactive consent — both scopes so user consents to all at once.
            await instance.acquireTokenRedirect({
              account: activeAccount,
              scopes: [apiScope, ...graphScopes],
            });
            return { authToken: null, graphToken: null }; // redirect in flight
          }
          throw err;
        }
      }

      // Legacy path: single acquireTokenSilent with Graph scopes + openid.
      const resp = await instance.acquireTokenSilent({ account: activeAccount, scopes, forceRefresh });
      let idToken = resp?.idToken;
      if (!idToken && !forceRefresh) {
        // idToken missing from cache — retry with force-refresh.
        const fresh = await instance.acquireTokenSilent({ account: activeAccount, scopes, forceRefresh: true });
        idToken = fresh?.idToken;
        return { authToken: idToken, graphToken: fresh?.accessToken };
      }
      return { authToken: idToken, graphToken: resp?.accessToken };
    };

    const reqId = axios.interceptors.request.use(async (config) => {
      if (config.url?.startsWith(apiPath + '/api/')) {
        const activeAccount = getActiveAccount();
        if (activeAccount) {
          try {
            const { authToken, graphToken } = await getTokens(activeAccount);
            config.headers = config.headers || {};
            if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
            if (graphToken) config.headers['X-Graph-Token'] = graphToken;
          } catch (e) {
            console.warn('[auth] token acquisition failed — request will proceed without auth:', e?.message ?? e);
          }
        }
      }
      return config;
    });

    const resId = axios.interceptors.response.use(
      (res) => res,
      async (err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          const originalRequest = err.config;

          // On 401 (stale / missing token), retry once with force-refreshed tokens
          // before treating it as a hard auth failure.
          // 403 (membership revoked) is a hard failure — skip the retry.
          if (status === 401 && !originalRequest._retried) {
            originalRequest._retried = true;
            const activeAccount = getActiveAccount();
            if (activeAccount) {
              try {
                const { authToken, graphToken } = await getTokens(activeAccount, true);
                if (authToken) {
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers.Authorization = `Bearer ${authToken}`;
                  if (graphToken) originalRequest.headers['X-Graph-Token'] = graphToken;
                  return axios(originalRequest);
                }
              } catch (refreshErr) {
                console.warn('[auth] Token refresh failed:', refreshErr?.message ?? refreshErr);
              }
            }
          }

          // Hard auth failure — clear local state and redirect to login.
          // 403 = membership explicitly revoked; show access-denied message.
          if (status === 403) sessionStorage.setItem('authDeniedMessage', 'access_denied');
          sessionStorage.removeItem('userInfo');
          sessionStorage.removeItem('userRole');
          setAccount(null);
          setUserProfile(null);
          setRole(null);
          try {
            await instance.logoutRedirect({ postLogoutRedirectUri: '/pass/login' });
          } catch {}
        }
        return Promise.reject(err);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, [instance]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const redirectResponse = await instance.handleRedirectPromise();

        if (redirectResponse?.account) {
          instance.setActiveAccount(redirectResponse.account);
          setAccount(redirectResponse.account);

          const { allowed, role: resolvedRole } = await validateGroupMembership(redirectResponse.account);
          if (!allowed) {
            sessionStorage.removeItem('userInfo');
            sessionStorage.removeItem('userRole');
            sessionStorage.setItem('authDeniedMessage', 'access_denied');
            await instance.logoutRedirect({
              postLogoutRedirectUri: '/pass/login',
            });
            return;
          }

          setRole(resolvedRole);
          sessionStorage.setItem('userRole', resolvedRole);
          await fetchUserProfile(redirectResponse.account);
          setAuthError('');
          // Only navigate if not already on a protected page (avoids a redundant reload
          // when acquireTokenRedirect cycles back through handleRedirectPromise while the
          // user is already on the dashboard).
          if (!window.location.pathname.startsWith('/pass/dashboard')) {
            window.location.replace('/pass/dashboard');
          }
          return;
        }

        const existingAccount = getActiveAccount();
        // Skip membership re-validation when already on the login page.
        // Calling logoutRedirect from /pass/login creates a redirect loop because
        // Azure AD logs out and immediately redirects back to /pass/login, where
        // initializeAuth runs again and finds the same account.
        // Protected pages (/pass/*) are validated below; the login page is public.
        const onLoginPage = window.location.pathname === '/pass/login' ||
                            window.location.pathname === '/';
        if (existingAccount && !onLoginPage) {
          instance.setActiveAccount(existingAccount);
          setAccount(existingAccount);

          // Always re-validate membership on every page load so that users removed
          // from the Azure AD group are kicked out immediately instead of staying in
          // until their sessionStorage expires. forceRefresh bypasses stale token claims.
          const { allowed, role: resolvedRole } = await validateGroupMembership(existingAccount, true);
          if (!allowed) {
            sessionStorage.removeItem('userInfo');
            sessionStorage.removeItem('userRole');
            sessionStorage.setItem('authDeniedMessage', 'access_denied');
            setAccount(null);
            setUserProfile(null);
            setRole(null);
            await instance.logoutRedirect({ postLogoutRedirectUri: '/pass/login' });
            return;
          }

          setRole(resolvedRole);
          sessionStorage.setItem('userRole', resolvedRole);
          await fetchUserProfile(existingAccount);
        } else if (existingAccount && onLoginPage) {
          // On the login page with a cached session — restore role/profile from
          // sessionStorage so the rest of the app is consistent, but skip the
          // Graph membership re-check (avoids the logoutRedirect loop).
          instance.setActiveAccount(existingAccount);
          setAccount(existingAccount);
          const cachedRole = sessionStorage.getItem('userRole');
          if (cachedRole) setRole(cachedRole);
          const cachedProfile = sessionStorage.getItem('userInfo');
          if (cachedProfile) {
            try { setUserProfile(JSON.parse(cachedProfile)); } catch {}
          }
        }
      } catch (error) {
        console.error('Error handling auth redirect', error);
        setAuthError('Authentication initialization failed.');
      } finally {
        setAuthLoading(false);
      }
    };

    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        instance.setActiveAccount(event.payload.account);
        setAccount(event.payload.account);
      }
    });

    initializeAuth();

    const forceLogout = async () => {
      sessionStorage.setItem('authDeniedMessage', 'access_denied');
      sessionStorage.removeItem('userInfo');
      sessionStorage.removeItem('userRole');
      setAccount(null);
      setUserProfile(null);
      setRole(null);
      await instance.logoutRedirect({ postLogoutRedirectUri: '/pass/login' });
    };

    const revalidateMembership = async () => {
      const activeAccount = getActiveAccount();
      if (!activeAccount) return;
      lastRevalidationRef.current = Date.now();
      try {
        const { allowed } = await validateGroupMembership(activeAccount, true);
        if (!allowed) await forceLogout();
      } catch {
        // Network errors are non-fatal; next interval or visibility event will retry.
      }
    };

    // Expose to context so Admin layout can call it on route changes.
    revalidateMembershipRef.current = revalidateMembership;

    // Re-validate every 5 minutes so removed users are kicked out of active sessions.
    const revalidateInterval = setInterval(revalidateMembership, 5 * 60 * 1000);

    // Re-validate immediately when the tab becomes visible after being hidden
    // (e.g. computer woke from sleep, user switched tabs for > 5 minutes).
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const elapsed = Date.now() - lastRevalidationRef.current;
      if (elapsed >= 5 * 60 * 1000) revalidateMembership();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      revalidateMembershipRef.current = null;
      clearInterval(revalidateInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  // Stable reference — safe to put in context and useEffect deps without causing re-renders.
  // Always calls the latest revalidateMembership closure via the ref.
  const revalidate = useCallback(() => { revalidateMembershipRef.current?.(); }, []);

  const login = async () => {
    try {
      setAuthError('');
      // Include apiScope (if configured) so the user consents to the API resource
      // during initial login — prevents InteractionRequiredAuthError on first API call.
      const loginScopes = apiScope ? [...new Set([...scopes, apiScope])] : scopes;
      await instance.loginRedirect({
        ...loginRequest,
        scopes: loginScopes,
      });
    } catch (error) {
      console.error('Azure AD login failed', error);
      setAuthError('Azure AD login failed.');
    }
  };

  const logout = async () => {
    try {
      sessionStorage.removeItem('userInfo');
      sessionStorage.removeItem('userRole');
      setAccount(null);
      setUserProfile(null);
      setRole(null);

      await instance.logoutRedirect({
        postLogoutRedirectUri: '/pass/login',
      });
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  const acquireToken = async () => {
    const activeAccount = getActiveAccount();

    if (!activeAccount) {
      await login();
      return null;
    }

    try {
      return await instance.acquireTokenSilent({
        ...loginRequest,
        account: activeAccount,
        scopes,
      });
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({
          ...loginRequest,
          scopes,
        });
        return null;
      }

      throw error;
    }
  };

  const value = useMemo(
    () => ({ account, userProfile, role, login, logout, acquireToken, authError, revalidate, authLoading }),
    [account, userProfile, role, authError, revalidate, authLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);